import React, { useEffect, useState } from "react";
import { qformat, DATE_FORMATS, formatRelativeVerbose } from "views_shared/app/javascript/utils/dateFormatters";
import LoadingState from "../shared/LoadingState";
import ErrorState from "../shared/ErrorState";
import StatusRow from "../shared/StatusRow";
import UserVerificationSection from "../new_users/UserVerificationSection";

// Utility functions

/**
 * Determine trust level based on user data
 * For media users, spam flags, leaker status, and top PRs eligibility are not applicable
 */
function getTrustLevel(userData, isMedia = false) {
  if (!userData)
    return { level: "unknown", label: "Unknown", className: "bg-secondary", borderClass: "border-secondary" };
  if (userData.deleted_at)
    return { level: "deleted", label: "Deleted", className: "bg-dark", borderClass: "border-dark" };
  if (userData.disabled_at)
    return { level: "disabled", label: "Disabled", className: "bg-danger", borderClass: "border-danger" };
  if (userData.under_audit_at)
    return { level: "under_audit", label: "Under Audit", className: "bg-danger", borderClass: "border-danger" };

  // Spam flags, leaker status, and top PRs are only relevant for promoters
  if (!isMedia) {
    if (userData.spam_flagged_count >= 3)
      return { level: "under_audit", label: "Under Audit", className: "bg-danger", borderClass: "border-danger" };
    if (userData.spam_flagged_count > 0)
      return { level: "flagged", label: "Flagged", className: "bg-warning text-dark", borderClass: "border-warning" };
    if (userData.suspected_leaker_dialog_enabled)
      return {
        level: "suspected_leaker",
        label: "Suspected Leaker",
        className: "bg-warning text-dark",
        borderClass: "border-warning",
      };
    if (userData.top_prs_ineligible)
      return { level: "restricted", label: "Restricted", className: "bg-secondary", borderClass: "border-secondary" };
  }

  if (userData.disabled_interactions_at)
    return { level: "limited", label: "Limited", className: "bg-info", borderClass: "border-info" };
  return { level: "trusted", label: "Trusted", className: "bg-success", borderClass: "border-success" };
}

// Card view (for overview page)

function TrustCardView({ userData, userSlug, isMedia }) {
  const trustLevel = getTrustLevel(userData, isMedia);
  const hasTrustIssues = trustLevel.level !== "trusted";

  const iconClass = hasTrustIssues ? "text-warning" : "text-success";
  const statusIcon = hasTrustIssues ? "fa-exclamation-triangle" : "fa-shield";

  return (
    <div className={`card mb-4 ${trustLevel.borderClass}`} style={{ borderLeftWidth: "4px" }}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h5 className="card-title">
              <i className={`fa ${statusIcon} ${iconClass} me-2`} />
              Trust Status: <span className={`badge ${trustLevel.className}`}>{trustLevel.label}</span>
            </h5>
            <div className="row mt-3">
              {/* Spam flags are only relevant for promoters */}
              {!isMedia && (
                <div className="col-auto">
                  <small className="text-muted d-block">Spam Flags</small>
                  <span
                    className={`fs-5 fw-semibold ${userData?.spam_flagged_count > 0 ? "text-danger" : "text-success"}`}
                  >
                    {userData?.spam_flagged_count || 0}
                  </span>
                </div>
              )}
              <div className="col-auto">
                <small className="text-muted d-block">Account</small>
                <span className={`fs-5 fw-semibold ${userData?.disabled_at ? "text-danger" : "text-success"}`}>
                  {userData?.disabled_at ? "Disabled" : "Active"}
                </span>
              </div>
              <div className="col-auto">
                <small className="text-muted d-block">Interactions</small>
                <span
                  className={`fs-5 fw-semibold ${userData?.disabled_interactions_at ? "text-warning" : "text-success"}`}
                >
                  {userData?.disabled_interactions_at ? "Disabled" : "Enabled"}
                </span>
              </div>
              {/* Top PRs eligibility is only relevant for promoters */}
              {!isMedia && (
                <div className="col-auto">
                  <small className="text-muted d-block">Top PRs</small>
                  <span
                    className={`fs-5 fw-semibold ${userData?.top_prs_ineligible ? "text-warning" : "text-success"}`}
                  >
                    {userData?.top_prs_ineligible ? "Ineligible" : "Eligible"}
                  </span>
                </div>
              )}
            </div>
          </div>
          <a href={`/admin/users/${userSlug}/trust`} className="btn btn-outline-primary btn-sm">
            Details <i className="fa fa-arrow-right ms-1" />
          </a>
        </div>
      </div>
    </div>
  );
}

// Full view components

function TrustOverviewSection({ userData, isMedia }) {
  const trustLevel = getTrustLevel(userData, isMedia);

  return (
    <div className="card mb-4">
      <div className="card-header">
        <h5 className="mb-0">
          <i className="fa fa-shield me-2" />
          Trust Overview
        </h5>
      </div>
      <div className="card-body">
        <div className="d-flex align-items-center mb-3">
          <span className="me-3">Overall Trust Status:</span>
          <span className={`badge ${trustLevel.className} fs-6`}>{trustLevel.label}</span>
        </div>

        <table className="table table-sm">
          <tbody>
            <StatusRow label="Account Active" value={!userData.disabled_at} />
            <StatusRow label="Interactions Enabled" value={!userData.disabled_interactions_at} />
            <StatusRow label="Not Deleted" value={!userData.deleted_at} />
            {/* Top PRs eligibility is only relevant for promoters */}
            {!isMedia && <StatusRow label="Eligible for Top PRs" value={!userData.top_prs_ineligible} />}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SpamFlagsSection({ userData }) {
  const hasSpamFlags = userData.spam_flagged_count > 0;
  const isUnderAudit = userData.spam_flagged_count >= 3;

  return (
    <div className="card mb-4">
      <div className="card-header">
        <h5 className="mb-0">
          <i className="fa fa-flag me-2" />
          Spam Flags
        </h5>
      </div>
      <div className="card-body">
        {isUnderAudit && (
          <div className="alert alert-danger mb-3" role="alert">
            <strong>User is under audit.</strong> This user has accumulated 3 or more spam flags and is invisible to
            other users until reviewed.
          </div>
        )}

        <table className="table table-sm">
          <tbody>
            <tr className={hasSpamFlags ? "table-warning" : ""}>
              <td>
                <strong>Spam Flag Count</strong>
              </td>
              <td>
                <span className={hasSpamFlags ? "text-danger fw-bold" : "text-success"}>
                  {userData.spam_flagged_count || 0}
                </span>
                {isUnderAudit && <span className="badge bg-danger ms-2">Under Audit Threshold</span>}
              </td>
            </tr>
            <tr>
              <td>Last Flagged</td>
              <td>
                {userData.spam_flagged_at ? (
                  <>
                    {qformat(userData.spam_flagged_at, DATE_FORMATS.timestamp)}
                    <small className="text-muted ms-2">({formatRelativeVerbose(userData.spam_flagged_at)})</small>
                  </>
                ) : (
                  <span className="text-muted">Never</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SuspectedLeakerSection({ userData }) {
  const isEnabled = userData.suspected_leaker_dialog_enabled;

  return (
    <div className="card mb-4">
      <div className="card-header">
        <h5 className="mb-0">
          <i className="fa fa-eye-slash me-2" />
          Suspected Leaker Status
        </h5>
      </div>
      <div className="card-body">
        {isEnabled && (
          <div className="alert alert-warning mb-3" role="alert">
            <strong>Suspected leaker dialog is enabled.</strong> This user will see a warning dialog when accessing
            certain features.
          </div>
        )}

        <table className="table table-sm">
          <tbody>
            <StatusRow label="Dialog Enabled" value={isEnabled} invertColors />
            <tr>
              <td>Times Enabled</td>
              <td>{userData.suspected_leaker_dialog_enabled_count || 0}</td>
            </tr>
            <tr>
              <td>Last Enabled</td>
              <td>
                {userData.suspected_leaker_dialog_enabled_last_at ? (
                  <>
                    {qformat(userData.suspected_leaker_dialog_enabled_last_at, DATE_FORMATS.timestamp)}
                    <small className="text-muted ms-2">
                      ({formatRelativeVerbose(userData.suspected_leaker_dialog_enabled_last_at)})
                    </small>
                  </>
                ) : (
                  <span className="text-muted">Never</span>
                )}
              </td>
            </tr>
            <tr>
              <td>Last Disabled</td>
              <td>
                {userData.suspected_leaker_dialog_disabled_last_at ? (
                  <>
                    {qformat(userData.suspected_leaker_dialog_disabled_last_at, DATE_FORMATS.timestamp)}
                    <small className="text-muted ms-2">
                      ({formatRelativeVerbose(userData.suspected_leaker_dialog_disabled_last_at)})
                    </small>
                  </>
                ) : (
                  <span className="text-muted">Never</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AccountRestrictionsSection({ userData, isMedia }) {
  return (
    <div className="card mb-4">
      <div className="card-header">
        <h5 className="mb-0">
          <i className="fa fa-ban me-2" />
          Account Restrictions
        </h5>
      </div>
      <div className="card-body">
        <table className="table table-sm">
          <tbody>
            <tr>
              <td>Account Disabled</td>
              <td>
                {userData.disabled_at ? (
                  <span className="text-danger">
                    <strong>Yes</strong> - {qformat(userData.disabled_at, DATE_FORMATS.timestamp)}
                  </span>
                ) : (
                  <span className="text-success">No</span>
                )}
              </td>
            </tr>
            <tr>
              <td>Interactions Disabled</td>
              <td>
                {userData.disabled_interactions_at ? (
                  <span className="text-warning">
                    <strong>Yes</strong> - {qformat(userData.disabled_interactions_at, DATE_FORMATS.timestamp)}
                  </span>
                ) : (
                  <span className="text-success">No</span>
                )}
              </td>
            </tr>
            <tr>
              <td>Account Deleted</td>
              <td>
                {userData.deleted_at ? (
                  <span className="text-danger">
                    <strong>Yes</strong> - {qformat(userData.deleted_at, DATE_FORMATS.timestamp)}
                  </span>
                ) : (
                  <span className="text-success">No</span>
                )}
              </td>
            </tr>
            {/* Top PRs eligibility is only relevant for promoters */}
            {!isMedia && (
              <tr>
                <td>Top PRs Ineligible</td>
                <td>
                  {userData.top_prs_ineligible ? (
                    <span className="text-warning">
                      <strong>Yes</strong> - Excluded from Top PRs lists
                    </span>
                  ) : (
                    <span className="text-success">No - Eligible for Top PRs</span>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TrustFullView({ userId, userData, isMedia, onVerificationReviewed }) {
  return (
    <div className="admin-user-trust">
      <TrustOverviewSection userData={userData} isMedia={isMedia} />
      <UserVerificationSection
        userId={userId}
        vetted={userData.vetted}
        vettedNote={userData.vetted_note}
        onReviewed={onVerificationReviewed}
      />
      {/* Spam flags and leaker status are only relevant for promoters */}
      {!isMedia && (
        <div className="row">
          <div className="col-md-6">
            <SpamFlagsSection userData={userData} />
          </div>
          <div className="col-md-6">
            <SuspectedLeakerSection userData={userData} />
          </div>
        </div>
      )}
      <AccountRestrictionsSection userData={userData} isMedia={isMedia} />
    </div>
  );
}

/**
 * AdminUserTrust - Trust component for admin user detail page
 *
 * Supports two modes:
 * - mode="card" (default for overview): Compact summary card with link to full view
 * - mode="full": Complete trust details with all sections
 *
 * For media users, certain sections are hidden as they are not applicable:
 * - Spam flags (media users can't be spam flagged)
 * - Suspected leaker status (only applies to promoters)
 * - Top PRs eligibility (only applies to promoters)
 *
 * @param {string} userId - User ID to fetch data for
 * @param {string} userSlug - User slug for generating links (required for card mode)
 * @param {boolean} isMedia - Whether this is a media user (hides promoter-specific fields)
 * @param {string} mode - "card" or "full" (default: "full")
 */
function AdminUserTrust({ userId, userSlug, isMedia = false, mode = "full" }) {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const isCardMode = mode === "card";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { supabaseClient } = globalThis.Qwoted;
        const { data, error: fetchError } = await supabaseClient.from("users").select().eq("id", userId).single();

        if (fetchError) {
          throw new Error(fetchError.message);
        }

        setUserData(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, reloadKey]);

  if (loading) return <LoadingState compact={isCardMode} label="Loading trust data..." />;
  if (error) return <ErrorState message={error} title="Unable to Load Trust Data" compact={isCardMode} />;

  if (isCardMode) {
    return <TrustCardView userData={userData} userSlug={userSlug} isMedia={isMedia} />;
  }

  return (
    <TrustFullView
      userId={userId}
      userData={userData}
      isMedia={isMedia}
      onVerificationReviewed={() => setReloadKey((key) => key + 1)}
    />
  );
}

export default AdminUserTrust;
